import { motion } from "framer-motion";
import { WorldMapScene } from "@/components/WorldMapScene";
import { JoinCta, ValidatorCta } from "@/components/Buttons";

const EASE = [0.22, 1, 0.36, 1];

const OBS_LABELS = [
  { text: "Road", x: "62%", y: "34%", dot: "bg-signal-cyan", delay: 0 },
  { text: "Vehicle", x: "26%", y: "26%", dot: "bg-signal-violet", delay: 1.8 },
  { text: "Building", x: "47%", y: "20%", dot: "bg-signal-cyan", delay: 3.4 },
  { text: "Infrastructure", x: "76%", y: "50%", dot: "bg-signal-violet", delay: 5 },
  { text: "Environment", x: "34%", y: "64%", dot: "bg-signal-green", delay: 6.6 },
];

export const Hero = () => {
  return (
    <section id="top" className="relative min-h-screen flex flex-col overflow-hidden bg-ink-950" data-testid="hero-section">
      <div className="absolute left-[-15%] top-[-25%] h-[520px] w-[520px] rounded-full bg-signal-violet/[0.07] blur-[160px] pointer-events-none" />
      <div className="absolute right-[-10%] bottom-[-20%] h-[460px] w-[460px] rounded-full bg-signal-cyan/[0.05] blur-[150px] pointer-events-none" />

      <div className="relative z-10 flex-1 max-w-7xl mx-auto w-full px-6 lg:px-8 pt-32 lg:pt-40 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10 items-center">
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="text-xs font-semibold uppercase tracking-[0.28em] text-steel"
            data-testid="hero-eyebrow"
          >
            Synaphy
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.25, ease: EASE }}
            className="mt-6 font-display font-bold tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-7xl text-mist"
            data-testid="hero-headline"
          >
            The physical world
            <br />
            is becoming{" "}
            <span className="text-gradient-data">data.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5, ease: EASE }}
            className="mt-7 max-w-md text-lg leading-relaxed text-steel"
            data-testid="hero-sub"
          >
            Synaphy builds infrastructure for capturing, understanding and
            verifying real-world data.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.65, ease: EASE }}
            className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4"
          >
            <JoinCta testid="hero-join-btn" className="justify-center sm:justify-start" />
            <ValidatorCta testid="hero-validator-btn" className="justify-center sm:justify-start" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.85 }}
            className="mt-8 flex items-center gap-2.5"
            data-testid="hero-status"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-signal-cyan opacity-60 soft-ping" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-signal-cyan" />
            </span>
            <span className="text-sm text-steel">
              Founding Network <span className="text-mist/70">·</span> Now open
            </span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.4, ease: EASE }}
          className="lg:col-span-6"
        >
          <div className="relative h-[340px] sm:h-[440px] lg:h-[520px]" data-testid="hero-visual">
            <WorldMapScene className="absolute inset-0" />
            {OBS_LABELS.map((l) => (
              <motion.span
                key={l.text}
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 1, 1, 0] }}
                transition={{ duration: 7, delay: 1.6 + l.delay, repeat: Infinity, times: [0, 0.12, 0.75, 1] }}
                className="absolute flex items-center gap-1.5 pointer-events-none"
                style={{ left: l.x, top: l.y }}
                data-testid={`hero-obs-${l.text.toLowerCase()}`}
              >
                <span className={`h-1 w-1 rounded-full ${l.dot}`} />
                <span className="text-[11px] font-medium text-mist/60">{l.text}</span>
              </motion.span>
            ))}
            <div className="absolute bottom-0 right-0 flex items-center gap-2 font-mono text-[9px] tracking-[0.18em] text-zinc-600 uppercase">
              <span>Raw observation</span>
              <span className="text-signal-cyan/70">→</span>
              <span>AI</span>
              <span className="text-signal-violet/70">→</span>
              <span>Verified intelligence</span>
            </div>
            <div className="absolute top-0 right-0 font-mono text-[9px] tracking-[0.18em] text-zinc-700 uppercase">
              Concept visualization
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
